import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Scale, MessageSquare, FileText, LayoutDashboard, LogOut, Building2, Search, FileSignature, Bell, Settings, ChevronDown, Menu, X, Workflow, GitCompare, Folder, FolderOpen, BookOpen, GitBranch, Languages, CheckSquare, Crown, Check, Trash2, Receipt, Users, ClipboardList, CreditCard
} from "lucide-react";
import TimerWidget from '@/components/TimerWidget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const roleColors = {
  super_admin: "bg-purple-100 text-purple-800",
  admin: "bg-purple-100 text-purple-800",
  avocat: "bg-blue-100 text-blue-800",
  juriste: "bg-green-100 text-green-800",
  relecteur: "bg-amber-100 text-amber-800",
  stagiaire: "bg-slate-100 text-slate-800"
};

const roleLabels = {
  super_admin: "Super Admin",
  admin: "Admin",
  avocat: "Avocat",
  juriste: "Juriste",
  relecteur: "Relecteur",
  stagiaire: "Stagiaire"
};

export default function Layout({ children, user, cabinet, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [sbTheme, setSbTheme] = useState(
    () => localStorage.getItem('lawliet_sidebar_theme') || 'light'
  );

  // P1.2 — Notifications state
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const apiHeaders = () => {
    const token = localStorage.getItem("lawliet_token");
    return { Authorization: `Bearer ${token}` };
  };

  const loadNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications`, {
        headers: apiHeaders(), params: { limit: 20 }, withCredentials: true
      });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread || 0);
    } catch { /* silent */ }
  }, []);

  // Poll notifications every 60s
  useEffect(() => {
    if (!user) return;
    loadNotifications();
    const interval = setInterval(loadNotifications, 60000);
    return () => clearInterval(interval);
  }, [user, loadNotifications]);

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/notifications/${id}/read`, {}, { headers: apiHeaders(), withCredentials: true });
      setNotifications(prev => prev.map(n => n.id === id ? {...n, read: true} : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch { /* silent */ }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`, {}, { headers: apiHeaders(), withCredentials: true });
      setNotifications(prev => prev.map(n => ({...n, read: true})));
      setUnreadCount(0);
    } catch { /* silent */ }
  };

  const deleteNotif = async (id, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/notifications/${id}`, { headers: apiHeaders(), withCredentials: true });
      setNotifications(prev => prev.filter(n => n.id !== id));
      setUnreadCount(prev => {
        const notif = notifications.find(n => n.id === id);
        return notif && !notif.read ? Math.max(0, prev - 1) : prev;
      });
    } catch { /* silent */ }
  };

  const NOTIF_ICONS = {
    legal_watch: Scale, legal_update: ClipboardList, document: FileText,
    contract: FileSignature, billing: CreditCard, system: Bell,
  };

  const navGroups = [
    {
      label: null,
      items: [
        { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard", testId: "nav-dashboard", color: "text-slate-500" },
      ]
    },
    {
      label: "Intelligence IA",
      items: [
        { path: "/chat", icon: MessageSquare, label: "Assistant IA", testId: "nav-chat", color: "text-blue-500" },
        { path: "/knowledge", icon: BookOpen, label: "Knowledge", testId: "nav-knowledge", color: "text-blue-500" },
        { path: "/legal-watch", icon: Bell, label: "Veille légale", testId: "nav-legal-watch", color: "text-blue-500" },
      ]
    },
    {
      label: "Documents",
      items: [
        { path: "/documents", icon: FileText, label: "Analyse", testId: "nav-documents", color: "text-emerald-500" },
        { path: "/document-comparison", icon: GitCompare, label: "Comparaison", testId: "nav-comparison", color: "text-emerald-500" },
        { path: "/bulk-processing", icon: Folder, label: "Traitement en masse", testId: "nav-bulk", color: "text-emerald-500" },
        { path: "/translation", icon: Languages, label: "Traduction", testId: "nav-translation", color: "text-emerald-500" },
        { path: "/search", icon: Search, label: "Recherche", testId: "nav-search", color: "text-emerald-500" },
      ]
    },
    {
      label: "Dossiers",
      items: [
        { path: "/dossiers", icon: FolderOpen, label: "Mes dossiers", testId: "nav-dossiers", color: "text-violet-500" },
        { path: "/clients", icon: Users, label: "Clients", testId: "nav-clients", color: "text-violet-500" },
        { path: "/facturation", icon: Receipt, label: "Facturation", testId: "nav-facturation", color: "text-violet-500" },
        { path: "/contracts", icon: FileSignature, label: "Contrats", testId: "nav-contracts", color: "text-violet-500" },
        { path: "/todos", icon: CheckSquare, label: "Tâches", testId: "nav-todos", color: "text-violet-500" },
      ]
    },
    {
      label: "Automatisation",
      items: [
        { path: "/workflows", icon: Workflow, label: "Workflows", testId: "nav-workflows", color: "text-amber-500" },
        { path: "/workflow-builder", icon: GitBranch, label: "Workflow Builder", testId: "nav-workflow-builder", color: "text-amber-500" },
      ]
    },
    ...(user?.role === "super_admin" ? [{
      label: "Administration",
      items: [
        { path: "/superadmin", icon: Crown, label: "Super Admin", testId: "nav-superadmin", color: "text-purple-500", special: true },
      ]
    }] : []),
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside
        data-sb-theme={sbTheme}
        className={`sb-premium hidden lg:flex lg:sticky lg:top-0 lg:h-screen flex-col transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-20'}`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4" style={{ borderBottom: '1px solid var(--sb-border)' }}>
          {sidebarOpen ? (
            <>
              <button
                onClick={() => navigate("/dashboard")}
                className="flex items-center gap-2 text-lg font-bold"
                style={{ color: 'var(--sb-logo-text)' }}
              >
                Lawliet<span style={{ color: 'var(--sb-logo-accent)' }}>.ai</span>
              </button>
              <Button variant="ghost" size="sm" onClick={() => setSidebarOpen(false)} className="h-8 w-8 p-0 text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => setSidebarOpen(true)} className="h-8 w-8 p-0 mx-auto text-slate-400">
              <Menu className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Nav groups */}
        <nav className="flex-1 overflow-y-auto px-3 py-3">
          {navGroups.map((group) => (
            <div key={group.label || 'main'}>
              {group.label && sidebarOpen && (
                <div className="sb-section-heading">{group.label}</div>
              )}
              {!group.label && !sidebarOpen && <div className="mt-2" />}
              {group.items.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                return (
                  <button
                    key={item.path}
                    data-testid={item.testId}
                    onClick={() => navigate(item.path)}
                    className={`sb-nav-item w-full text-left ${isActive ? 'active' : ''} ${!sidebarOpen ? 'justify-center px-0' : ''}`}
                  >
                    <Icon className={`h-4 w-4 flex-shrink-0 ${isActive ? '' : item.color}`} />
                    {sidebarOpen && <span>{item.label}</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Settings links */}
        {sidebarOpen && (
          <div className="px-3 pb-2">
            <div className="sb-section-heading" style={{ borderTop: '1px solid var(--sb-section-border)' }}>Paramètres</div>
            <button onClick={() => navigate('/cabinet')} className="sb-nav-item w-full text-left">
              <Building2 className="h-4 w-4 flex-shrink-0 text-slate-400" />
              <span>Cabinet</span>
            </button>
            <button onClick={() => navigate('/settings')} className="sb-nav-item w-full text-left">
              <Settings className="h-4 w-4 flex-shrink-0 text-slate-400" />
              <span>Paramètres</span>
            </button>
          </div>
        )}

        {/* User profile */}
        {sidebarOpen && user && (
          <div className="sb-user-card">
            <div className="flex items-center gap-2.5">
              <div className="sb-avatar">
                {user.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold truncate" style={{ color: 'var(--sb-user-name)' }}>{user.full_name}</p>
                <p className="text-xs truncate" style={{ color: 'var(--sb-user-role)' }}>{roleLabels[user.role]} · {cabinet?.name}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={onLogout} className="h-7 w-7 p-0 text-slate-400 hover:text-red-500 flex-shrink-0">
                <LogOut className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileSidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black bg-opacity-50" onClick={() => setMobileSidebarOpen(false)}>
          <aside className="w-64 h-full bg-white" onClick={(e) => e.stopPropagation()}>
            {/* Mobile Sidebar Header */}
            <div className="h-16 border-b border-slate-200 flex items-center justify-between px-4">
              <div className="flex items-center space-x-2">
                <span className="text-lg font-bold text-blue-900">Lawliet<span className="text-blue-600">.ai</span></span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMobileSidebarOpen(false)}
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Mobile Cabinet Info */}
            {cabinet && (
              <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Cabinet</p>
                <p className="text-sm font-medium text-slate-900 mt-1">{cabinet.name}</p>
              </div>
            )}

            {/* Mobile Navigation */}
            <nav className="py-4">
              <div className="space-y-4 px-3">
                {navGroups.map((group) => (
                  <div key={group.label}>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest px-3 mb-1">
                      {group.label}
                    </p>
                    <div className="space-y-0.5">
                      {group.items.map((item) => {
                        const Icon = item.icon;
                        const isActive = location.pathname === item.path ||
                          (item.path === "/chat" && location.pathname.startsWith("/chat"));
                        return (
                          <button
                            key={item.path}
                            onClick={() => {
                              navigate(item.path);
                              setMobileSidebarOpen(false);
                            }}
                            data-testid={item.testId}
                            className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors ${
                              isActive
                                ? 'bg-blue-600 text-white'
                                : 'text-slate-700 hover:bg-slate-100'
                            }`}
                          >
                            <Icon className="h-4 w-4" />
                            <span className="text-sm font-medium">{item.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </nav>
          </aside>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
          <div className="h-16 px-4 sm:px-6 lg:px-8 flex items-center justify-between">
            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden"
              onClick={() => setMobileSidebarOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </Button>

            {/* Mobile Logo (visible only on mobile when sidebar is closed) */}
            <div className="lg:hidden flex items-center space-x-2">
              <img src="/lawliet-mascot.png" alt="Lawliet.ai" className="h-6 w-6 rounded-lg" />
              <span className="text-base font-bold text-blue-900">Lawliet<span className="text-blue-600">.ai</span></span>
            </div>

            {/* Spacer for desktop */}
            <div className="hidden lg:block flex-1"></div>

            {/* Header right: Notifications + User menu */}
            <div className="flex items-center gap-2">
              {/* Notifications Bell */}
              <div className="relative" id="notif-container">
                <Button
                  variant="ghost" size="sm"
                  className="relative h-9 w-9 p-0 rounded-full hover:bg-slate-100"
                  id="notif-bell-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setNotifOpen(prev => {
                      if (!prev) loadNotifications();
                      return !prev;
                    });
                  }}
                  aria-label="Notifications"
                >
                  <Bell className="h-4 w-4 text-slate-600" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 h-4 w-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </Button>

                {/* Click-outside overlay */}
                {notifOpen && (
                  <div className="fixed inset-0 z-40" onClick={() => setNotifOpen(false)} />
                )}

                {/* Notifications panel */}
                {notifOpen && (
                  <div className="absolute right-0 top-11 w-80 bg-white border border-slate-200 rounded-xl shadow-2xl z-50 overflow-hidden" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
                      <span className="text-sm font-semibold text-slate-800">
                        Notifications {unreadCount > 0 && <span className="text-blue-600">({unreadCount})</span>}
                      </span>
                      {unreadCount > 0 && (
                        <button onClick={markAllRead} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                          <Check className="h-3 w-3" />Tout lire
                        </button>
                      )}
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="py-8 text-center text-slate-400 text-sm">
                          <Bell className="h-8 w-8 mx-auto mb-2 opacity-30" />
                          Aucune notification
                        </div>
                      ) : (
                        notifications.map(n => (
                          <div
                            key={n.id}
                            onClick={() => { markRead(n.id); if (n.link) navigate(n.link); setNotifOpen(false); }}
                            className={`group flex items-start gap-3 px-4 py-3 border-b border-slate-50 cursor-pointer hover:bg-slate-50 transition-colors ${!n.read ? "bg-blue-50/40" : ""}`}
                          >
                            {(() => { const NIcon = NOTIF_ICONS[n.type] || Bell; return <NIcon className="h-4 w-4 mt-0.5 flex-shrink-0 text-slate-400" />; })()}
                            <div className="flex-1 min-w-0">
                              <p className={`text-xs font-semibold truncate ${!n.read ? "text-blue-900" : "text-slate-700"}`}>{n.title}</p>
                              <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.message}</p>
                              <p className="text-[10px] text-slate-400 mt-1">{n.created_at?.slice(0, 16)}</p>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {!n.read && <div className="h-2 w-2 bg-blue-600 rounded-full" />}
                              <button
                                onClick={e => deleteNotif(n.id, e)}
                                className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-50 rounded text-slate-300 hover:text-red-500 transition-opacity"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    {notifications.length > 0 && (
                      <div className="px-4 py-2 border-t border-slate-100 bg-slate-50 text-center">
                        <button onClick={() => { navigate("/legal-watch"); setNotifOpen(false); }} className="text-xs text-blue-600 hover:underline">
                          Voir la veille légale →
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* User dropdown menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center space-x-2 px-3 py-2 bg-slate-50 hover:bg-slate-100 rounded-lg transition cursor-pointer">
                    <div className="text-right">
                      <div className="flex items-center space-x-2">
                        <p className="text-xs font-medium text-slate-900">{user.full_name}</p>
                        <Badge className={`${roleColors[user.role]} text-xs h-4 px-1.5`}>
                          {roleLabels[user.role]}
                        </Badge>
                      </div>
                    </div>
                    <ChevronDown className="h-4 w-4 text-slate-500" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium">{user.full_name}</p>
                      <p className="text-xs text-slate-500">{user.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/cabinet')} className="cursor-pointer">
                    <Building2 className="mr-2 h-4 w-4" />
                    <span>Cabinet</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/settings')} className="cursor-pointer">
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Paramètres</span>
                  </DropdownMenuItem>
                  {user.role === "super_admin" && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate('/superadmin')} className="cursor-pointer text-purple-700">
                        <Crown className="mr-2 h-4 w-4" />
                        <span>Super Admin</span>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onLogout} className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50">
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Se déconnecter</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Main content */}
        <main className="flex-1 overflow-auto">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {children}
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-white border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="text-center text-sm text-slate-500">
              <p>Lawliet.ai v2.0 - Intelligence Juridique Européenne</p>
              <p className="mt-1">Multi-cabinets • Sécurité renforcée • Conforme RGPD</p>
            </div>
          </div>
        </footer>
      </div>
      <TimerWidget />
    </div>
  );
}
